package br.com.htms.inspeccaocampo.ui.dashboards

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.htms.inspeccaocampo.data.ocorrencias.Ocorrencia
import br.com.htms.inspeccaocampo.ui.PersonagemCorpoImagem
import br.com.htms.inspeccaocampo.ui.kit.CartaoSecao
import br.com.htms.inspeccaocampo.ui.kit.FmtDash
import br.com.htms.inspeccaocampo.ui.theme.PortalCores

private enum class ParteCorpo(val rotulo: String) {
    CABECA("Cabeça"),
    OLHOS("Olhos"),
    PESCOCO("Pescoço"),
    TRONCO("Tronco"),
    ABDOMEN("Abdômen"),
    BRACOS("Braços"),
    MAOS("Mãos"),
    PERNAS("Pernas"),
    PES("Pés"),
    OUTROS("Outros / N.I.")
}

private data class ZonaCorpo(val parte: ParteCorpo, val x: Float, val y: Float, val w: Float, val h: Float)

/** Áreas sobre o desenho, na proporção real da imagem 465×703. Medidas no contorno do personagem. */
private val ZONAS = listOf(
    ZonaCorpo(ParteCorpo.CABECA, 0.32f, 0.004f, 0.25f, 0.145f),
    ZonaCorpo(ParteCorpo.TRONCO, 0.33f, 0.300f, 0.24f, 0.100f),
    ZonaCorpo(ParteCorpo.ABDOMEN, 0.32f, 0.400f, 0.25f, 0.195f),
    ZonaCorpo(ParteCorpo.BRACOS, 0.228f, 0.335f, 0.100f, 0.255f),
    ZonaCorpo(ParteCorpo.BRACOS, 0.565f, 0.335f, 0.095f, 0.255f),
    ZonaCorpo(ParteCorpo.PERNAS, 0.300f, 0.688f, 0.130f, 0.230f),
    ZonaCorpo(ParteCorpo.PERNAS, 0.445f, 0.688f, 0.125f, 0.230f),
    ZonaCorpo(ParteCorpo.PESCOCO, 0.400f, 0.252f, 0.100f, 0.050f),
    ZonaCorpo(ParteCorpo.OLHOS, 0.355f, 0.158f, 0.200f, 0.048f),
    ZonaCorpo(ParteCorpo.MAOS, 0.228f, 0.598f, 0.090f, 0.090f),
    ZonaCorpo(ParteCorpo.MAOS, 0.560f, 0.598f, 0.090f, 0.090f),
    ZonaCorpo(ParteCorpo.PES, 0.180f, 0.922f, 0.210f, 0.072f),
    ZonaCorpo(ParteCorpo.PES, 0.420f, 0.922f, 0.250f, 0.072f)
)

private val PROPORCAO = 465f / 703f
private val AZUL_BARRA = Color(0xFF1E5AA8)

@Composable
internal fun MapaPartesCorpo(
    ocorrencias: List<Ocorrencia>,
    obraDe: (Ocorrencia) -> String,
    onObra: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    val porParte = remember(ocorrencias) {
        ParteCorpo.entries.associateWith { parte ->
            ocorrencias.count { parte in partesDe(it.campo("parteCorpo")) }
        }
    }
    var escolhida by remember { mutableStateOf<ParteCorpo?>(null) }
    val parte = escolhida
    val daParte = remember(ocorrencias, parte) {
        if (parte == null) emptyList() else ocorrencias.filter { parte in partesDe(it.campo("parteCorpo")) }
    }
    val ranking = contagem(daParte, obraDe)
    val qtd = if (parte == null) 0 else porParte[parte] ?: 0

    CartaoSecao(
        "Partes do corpo atingidas",
        modifier,
        "Clique em uma parte do corpo para ver quantos eventos ocorreram."
    ) {
        Column(
            Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(12.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            FiguraPartes(
                { nova -> escolhida = if (escolhida == nova) null else nova },
                Modifier.fillMaxWidth()
            )
            Text(
                "Outros / não informado",
                Modifier
                    .clip(RoundedCornerShape(6.dp))
                    .toque { escolhida = if (escolhida == ParteCorpo.OUTROS) null else ParteCorpo.OUTROS }
                    .padding(horizontal = 8.dp, vertical = 4.dp),
                color = PortalCores.AzulIcone,
                fontWeight = FontWeight.SemiBold,
                style = MaterialTheme.typography.bodyMedium
            )
            if (parte != null) {
                PainelParte(parte, qtd, ranking, onObra, Modifier.fillMaxWidth())
            }
        }
    }
}

@Composable
private fun FiguraPartes(onEscolher: (ParteCorpo) -> Unit, modifier: Modifier) {
    BoxWithConstraints(modifier) {
        val alturaCheia = 460.dp
        val larguraCheia = alturaCheia * PROPORCAO
        val escala = if (maxWidth < larguraCheia) maxWidth / larguraCheia else 1f
        val altura = alturaCheia * escala
        val larguraImg = altura * PROPORCAO
        Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
            Box(
                Modifier
                    .width(larguraImg)
                    .height(altura)
                    .clip(RoundedCornerShape(12.dp))
                    .background(Color.White)
            ) {
                PersonagemCorpoImagem(Modifier.fillMaxSize())
                ZONAS.forEach { z ->
                    Box(
                        Modifier
                            .offset(x = larguraImg * z.x, y = altura * z.y)
                            .size(width = larguraImg * z.w, height = altura * z.h)
                            .toque { onEscolher(z.parte) }
                    )
                }
            }
        }
    }
}

@Composable
private fun Modifier.toque(onClick: () -> Unit): Modifier {
    val origem = remember { MutableInteractionSource() }
    return clickable(interactionSource = origem, indication = null, onClick = onClick)
}

@Composable
private fun PainelParte(
    parte: ParteCorpo,
    qtd: Int,
    ranking: List<Pair<String, Double>>,
    onObra: (String) -> Unit,
    modifier: Modifier
) {
    Column(modifier, verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text(parte.rotulo, color = PortalCores.Navy, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.headlineSmall)
        Text(
            if (qtd == 1) "1 evento" else "${FmtDash.qtd(qtd)} eventos",
            color = if (qtd > 0) PortalCores.AzulIcone else PortalCores.CinzaTexto,
            fontWeight = FontWeight.SemiBold,
            style = MaterialTheme.typography.titleMedium
        )
        Text("Obras com mais eventos", color = PortalCores.CinzaTexto, style = MaterialTheme.typography.bodySmall)
        if (ranking.isEmpty()) {
            Text("Nenhum evento desta parte no período filtrado.", color = PortalCores.CinzaTexto)
            return@Column
        }
        val maior = ranking.maxOf { it.second }.takeIf { it > 0 } ?: 1.0
        ranking.take(8).forEach { (obra, valor) ->
            Column(
                Modifier.fillMaxWidth().clip(RoundedCornerShape(8.dp)).clickable { onObra(obra) }.padding(horizontal = 4.dp, vertical = 3.dp),
                verticalArrangement = Arrangement.spacedBy(3.dp)
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(
                        obra, Modifier.weight(1f), maxLines = 1, overflow = TextOverflow.Ellipsis,
                        style = MaterialTheme.typography.bodyMedium, color = PortalCores.Texto
                    )
                    Text(FmtDash.qtd(valor), fontWeight = FontWeight.SemiBold, color = AZUL_BARRA)
                }
                Box(Modifier.fillMaxWidth().height(8.dp).background(PortalCores.CabecalhoTabela, RoundedCornerShape(4.dp))) {
                    Box(
                        Modifier
                            .fillMaxWidth((valor / maior).toFloat().coerceIn(0.04f, 1f))
                            .height(8.dp)
                            .background(AZUL_BARRA, RoundedCornerShape(4.dp))
                    )
                }
            }
        }
    }
}

private fun partesDe(texto: String): Set<ParteCorpo> {
    val t = texto.trim()
    if (t.isEmpty() || t.equals("N/A", true) || t.equals("NA", true) || t.equals("N.I.", true)) return emptySet()
    val n = semAcento(t)
    val achou = linkedSetOf<ParteCorpo>()
    fun tem(vararg p: String) = p.any { n.contains(it) }
    if (tem("olho", "vista", "visao")) achou += ParteCorpo.OLHOS
    if (tem("cabeca", "cranio", "rosto", "testa") || palavra(n, "face")) achou += ParteCorpo.CABECA
    if (tem("pescoco", "cervical")) achou += ParteCorpo.PESCOCO
    if (tem("mao", "dedo", "punho", "palma")) achou += ParteCorpo.MAOS
    if (tem("braco", "antebraco", "cotovelo", "ombro")) achou += ParteCorpo.BRACOS
    if (tem("perna", "joelho", "coxa", "canela")) achou += ParteCorpo.PERNAS
    if (tem("tornozelo") || palavra(n, "pe") || palavra(n, "pes")) achou += ParteCorpo.PES
    if (tem("abdomen", "barriga", "lombar", "cintura")) achou += ParteCorpo.ABDOMEN
    if (tem("tronco", "torax", "peito", "costas", "coluna", "dorso")) achou += ParteCorpo.TRONCO
    if (achou.isEmpty()) achou += ParteCorpo.OUTROS
    return achou
}

private fun palavra(texto: String, alvo: String): Boolean =
    Regex("(^|[^a-z])${Regex.escape(alvo)}([^a-z]|$)").containsMatchIn(texto)

private fun semAcento(s: String): String = s.lowercase()
    .replace('á', 'a').replace('à', 'a').replace('ã', 'a').replace('â', 'a')
    .replace('é', 'e').replace('ê', 'e')
    .replace('í', 'i')
    .replace('ó', 'o').replace('õ', 'o').replace('ô', 'o')
    .replace('ú', 'u')
    .replace('ç', 'c')
